/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from '@web-stories-wp/react';

function useThumbnailLoadGate({
  resourceIds = [],
  resetKey,
  isActive = true,
}) {
  const readyIdsRef = useRef(new Set());
  const [readyCount, setReadyCount] = useState(0);
  const resourceIdSet = useMemo(
    () => new Set(resourceIds.filter(Boolean)),
    [resourceIds]
  );
  const prevResetKeyRef = useRef(resetKey);

  useEffect(() => {
    if (prevResetKeyRef.current !== resetKey) {
      prevResetKeyRef.current = resetKey;
      readyIdsRef.current = new Set();
      setReadyCount(0);
    }
  }, [resetKey]);

  useEffect(() => {
    const readyIds = readyIdsRef.current;
    let changed = false;
    readyIds.forEach((id) => {
      if (!resourceIdSet.has(id)) {
        readyIds.delete(id);
        changed = true;
      }
    });

    if (changed) {
      setReadyCount(readyIds.size);
    }
  }, [resourceIdSet]);

  const onThumbnailReady = useCallback(
    (id) => {
      if (!id || !resourceIdSet.has(id)) {
        return;
      }
      const readyIds = readyIdsRef.current;
      if (readyIds.has(id)) {
        return;
      }
      readyIds.add(id);
      setReadyCount(readyIds.size);
    },
    [resourceIdSet]
  );

  const isBatchReady =
    resourceIdSet.size === 0 || readyCount >= resourceIdSet.size;

  return { isBatchReady: isActive ? isBatchReady : false, onThumbnailReady };
}

export default useThumbnailLoadGate;
